// ============================================================================
// DESIGN SYSTEM PRIMITIVES
// ============================================================================
// Editorial Institutional Trading UI - Financial journalism meets live markets
// Serious, analytical, authoritative — not decorative

// ============================================================================
// EDITORIAL PALETTE - Newspaper & Financial Publication Colors
// ============================================================================
// Off-white "paper" backgrounds, near-black "ink" text, editorial grays

export const Editorial = {
  // Surface Hierarchy (Paper tones)
  Paper: '#fafaf9',           // Main background - off-white paper
  PaperElevated: '#f5f5f4',   // Slightly darker paper - elevated cards
  PaperSubdued: '#e7e5e4',    // Subdued paper - inactive/disabled areas

  // Ink Hierarchy (Text tones)
  Ink: '#171717',             // Primary text - near-black ink
  InkMedium: '#404040',       // Secondary text - medium ink
  InkLight: '#737373',        // Tertiary text - light ink
  InkFaint: '#a3a3a3',        // Faint text - hints, placeholders

  // Rules & Dividers (Editorial lines)
  Rule: '#d4d4d4',            // Standard rule - dividers, borders
  RuleStrong: '#a3a3a3',      // Strong rule - emphasis
  RuleFaint: '#e5e5e5',       // Faint rule - subtle separation

  // Semantic Colors (Muted, editorial-appropriate)
  Profit: '#16a34a',          // Muted green - profits, gains, buy
  Loss: '#991b1b',            // Deep editorial red - losses, declines, sell
  Warning: '#d97706',         // Muted amber - warnings, caution
  Info: '#525252',            // Neutral gray - information (no blue)
  Neutral: '#525252',         // Neutral gray - balanced, no change

  // Editorial Accent (Living ink - active, hover, focus, LIVE markers)
  Red: '#991b1b',             // Deep editorial red - primary accent, ink pressure
  RedHover: '#7f1d1d',        // Darker red on deeper interaction
  RedBright: '#dc2626',       // Brighter red for live/active markers
} as const;

// ============================================================================
// INVERTED PALETTE - For Dark Data Sections
// ============================================================================
// Some financial data (charts, order books) benefit from inverted presentation

export const EditorialInverted = {
  // Inverted surfaces
  Surface: '#171717',         // Dark surface
  SurfaceElevated: '#262626', // Slightly lighter dark

  // Inverted text
  Text: '#fafaf9',            // Light text on dark
  TextSecondary: '#a3a3a3',   // Secondary light text
  TextTertiary: '#737373',    // Tertiary light text

  // Rules on dark
  Rule: '#404040',            // Dividers on dark
} as const;

// ============================================================================
// BORDER RADIUS STANDARDS
// ============================================================================
// Zero radius - sharp, rectangular edges only

export const BorderRadius = {
  None: '0',                  // All elements - sharp rectangular geometry
} as const;

// ============================================================================
// TYPOGRAPHY SCALE
// ============================================================================
// Editorial hierarchy - headlines to captions

export const Typography = {
  // Font families
  FontSans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  FontMono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Courier New", monospace',

  // Scale (rem units)
  DisplayLarge: '2.5rem',     // 40px - major headlines
  DisplayMedium: '2rem',      // 32px - section headlines
  HeadlineLarge: '1.5rem',    // 24px - subsection headlines
  HeadlineMedium: '1.25rem',  // 20px - card headlines
  HeadlineSmall: '1.125rem',  // 18px - minor headlines
  BodyLarge: '1rem',          // 16px - primary body text
  BodyMedium: '0.9375rem',    // 15px - secondary body text
  BodySmall: '0.875rem',      // 14px - small body text
  Caption: '0.8125rem',       // 13px - captions, labels
  Fine: '0.75rem',            // 12px - fine print, meta

  // Weights
  WeightRegular: '400',       // Body text
  WeightMedium: '500',        // Emphasis
  WeightSemibold: '600',      // Subheadings
  WeightBold: '700',          // Headlines

  // Line heights
  LeadingTight: '1.2',        // Headlines
  LeadingNormal: '1.5',       // Body text
  LeadingRelaxed: '1.7',      // Long-form reading
} as const;

// ============================================================================
// SPACING SCALE
// ============================================================================
// Based on 4px grid system - rhythm and vertical spacing

export const Spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '3rem',    // 48px
  '3xl': '4rem',    // 64px
  '4xl': '6rem',    // 96px
} as const;

// ============================================================================
// MOTION & TRANSITIONS
// ============================================================================
// Subtle, purposeful motion - opacity and position only

export const Motion = {
  DurationFast: '150ms',
  DurationNormal: '250ms',
  DurationSlow: '350ms',
  EasingStandard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  EasingDecelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  EasingAccelerate: 'cubic-bezier(0.4, 0.0, 1, 1)',
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Class name merger utility
type ClassValue = string | number | boolean | undefined | null | ClassValue[];

export function cn(...inputs: ClassValue[]): string {
  return inputs
    .flat()
    .filter(Boolean)
    .join(' ')
    .trim();
}

// ============================================================================
// LEGACY COMPATIBILITY (Temporary - for gradual migration)
// ============================================================================
// Map old NavyPalette to Editorial for components not yet migrated

export const NavyPalette = {
  Deep: Editorial.Paper,
  Elevated: Editorial.PaperElevated,
  Raised: Editorial.PaperSubdued,
  Border: Editorial.Rule,
  Success: Editorial.Profit,
  Danger: Editorial.Loss,
  Warning: Editorial.Warning,
  Info: Editorial.Info,
  TextPrimary: Editorial.Ink,
  TextSecondary: Editorial.InkMedium,
  TextTertiary: Editorial.InkLight,
  TextMuted: Editorial.InkFaint,
} as const;
